'use client';

import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import * as haptics from '@/lib/native/haptics';

/**
 * A toast is for news that needs no answer: "Copied", "Import finished", or a
 * change with an Undo. It is NOT for an error the owner has to act on — that
 * is a notice (`useNotice` / `useReportFailure` in
 * `src/components/notice/NoticeProvider.tsx`), and not for a confirmation of
 * something already visible on screen, which needs nothing at all.
 * The rules: `docs/notices-and-toasts.md`.
 */

type ToastVariant = 'success' | 'info';

/**
 * What can be on screen. `error` is not something a call site can ask for any
 * more — the sweep that ended on 1 October 2026 took every error toast out of
 * the product (#42). The one way an error still reaches a toast is
 * `useReportFailure()` answering a plain 4xx with the server's own sentence,
 * through `failureToast` below.
 */
type ShownVariant = ToastVariant | 'error';

export interface ToastOptions {
  /** One short action, usually "Undo". Runs and closes the toast. */
  action?: { label: string; onClick: () => void };
}

interface Toast {
  id: number;
  message: string;
  variant: ShownVariant;
  action?: ToastOptions['action'];
}

/**
 * News only. There is no `'error'`: a failed request goes through
 * `useReportFailure()`, a form error goes beside the field, and anything with
 * something to do is a `notice()`. See docs/notices-and-toasts.md.
 */
export type ToastFn = (message: string, variant?: ToastVariant, options?: ToastOptions) => void;

interface ToastContextValue {
  toast: ToastFn;
  /**
   * @internal For `useReportFailure()` only: a plain refusal (a 4xx the
   * server explained) shown in the server's words. Nothing else calls this —
   * `a-new-toast-cannot-bypass-the-notice.test.ts` holds that line.
   */
  failureToast: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const noopToast: ToastContextValue = { toast: () => {}, failureToast: () => {} };

export function useToast() {
  const ctx = useContext(ToastContext);
  // Return no-op during SSR when provider isn't mounted yet
  return ctx || noopToast;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(0);

  const show = useCallback((message: string, variant: ShownVariant, options?: ToastOptions) => {
    const id = nextId.current++;
    setToasts(prev => [...prev, { id, message, variant, action: options?.action }]);
    if (variant === 'success') haptics.success();
    else if (variant === 'error') haptics.error();
  }, []);
  const toast = useCallback<ToastFn>((message, variant = 'success', options) => show(message, variant, options), [show]);
  const failureToast = useCallback((message: string) => show(message, 'error'), [show]);

  const dismiss = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toast, failureToast }}>
      {children}
      {/* A live region, so a screen reader hears what a sighted user sees. */}
      <div className="toast-container" role="status" aria-live="polite">
        {toasts.map(t => (
          <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: number) => void }) {
  const [leaving, setLeaving] = useState(false);
  // An Undo needs long enough to be reached for.
  const life = toast.action ? 6000 : 4000;

  useEffect(() => {
    const timer = setTimeout(() => setLeaving(true), life - 300);
    const remove = setTimeout(() => onDismiss(toast.id), life);
    return () => { clearTimeout(timer); clearTimeout(remove); };
  }, [toast.id, onDismiss, life]);

  const handleDismiss = () => {
    setLeaving(true);
    setTimeout(() => onDismiss(toast.id), 300);
  };

  return (
    <div className={`toast toast-${toast.variant}${leaving ? ' toast-leave' : ''}`}>
      <span className="toast-icon">
        {toast.variant === 'success' && (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
        )}
        {toast.variant === 'error' && (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
        )}
        {toast.variant === 'info' && (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
        )}
      </span>
      <span className="toast-text">{toast.message}</span>
      {toast.action && (
        <button
          type="button"
          className="toast-action"
          onClick={() => { toast.action!.onClick(); handleDismiss(); }}
        >
          {toast.action.label}
        </button>
      )}
      <button type="button" className="toast-close" aria-label="Dismiss" onClick={handleDismiss}>&times;</button>
    </div>
  );
}
